import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { SpeakingExercise } from '../../mockLessonContent';
import { BLUE, cardSx, INTER, NAVY, POPPINS, SLATE, SURFACE_SOFT, WARNING_TEXT, YELLOW } from '../../studentStyles';
import AudioPlayButton from '../AudioPlayButton';
import RecordAnswerPanel from './RecordAnswerPanel';
import SpeakingHero from './SpeakingHero';

interface SpeakingExerciseCardProps {
    exercise: SpeakingExercise;
    onReady: () => void;
}

// "Prompt, Speak With Grade" and its ungraded twin "Prompt, Speak Anything, No Grade".
export default function SpeakingExerciseCard({ exercise, onReady }: SpeakingExerciseCardProps) {
    return (
        <Box sx={{ ...cardSx, overflow: 'hidden' }}>
            <SpeakingHero
                label={exercise.graded ? 'SPEAKING PRACTICE' : 'FREE SPEAKING · NOT SCORED'}
                hanzi={exercise.heroHanzi}
                pinyin={exercise.heroPinyin}
            />

            <Box sx={{ p: 2.5 }}>
                {/* Model speaker */}
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '14px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${BLUE}1A`,
                    }}>
                        <VolumeUpRoundedIcon sx={{ color: BLUE }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>
                            {exercise.speaker.nameHanzi}
                        </Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE }}>
                            {exercise.speaker.nameEnglish}
                        </Typography>
                    </Box>
                    <AudioPlayButton ariaLabel="Play model audio" size={40} />
                </Stack>

                {/* Model line */}
                <Box sx={{ mt: 2.5, p: 2.25, borderRadius: '16px', backgroundColor: SURFACE_SOFT }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 20, color: NAVY, lineHeight: 1.5 }}>
                        {exercise.prompt.hanzi}
                    </Typography>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.75 }}>
                        {exercise.prompt.english}
                    </Typography>
                </Box>

                {/* Response starters (exit-check exercises provide none) */}
                {exercise.starters.length > 0 && (
                    <>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE, mt: 2.5, mb: 1 }}>
                            RESPONSE STARTERS
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {exercise.starters.map(starter => (
                                <Chip key={starter} label={starter} sx={{ fontFamily: POPPINS, color: BLUE, backgroundColor: `${BLUE}14` }} />
                            ))}
                        </Stack>
                    </>
                )}

                {/* teacher_hint_prompt */}
                {exercise.hint && (
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 2.5, p: 1.75, borderRadius: '14px', backgroundColor: `${YELLOW}26` }}>
                        <LightbulbRoundedIcon sx={{ color: WARNING_TEXT, fontSize: 20, flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: INTER, fontSize: 13, color: NAVY }}>
                            {exercise.hint}
                        </Typography>
                    </Stack>
                )}

                <RecordAnswerPanel grades={exercise.grades} graded={exercise.graded} onScored={onReady} />
            </Box>
        </Box>
    );
}
